/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { StatusBadge, EmptyState, PageHeader } from "./shared/components";
import {
  getHrInitiatedCases,
  HrInitiatedCaseItem,
} from "./services/eSeparation.service";

const FILTERS = [
  "All",
  "Termination",
  "Death",
  "Deputation",
  "Retirement",
  "Transfer-Out",
] as const;
type Filter = (typeof FILTERS)[number];

const TYPE_META: Record<
  string,
  { label: string; icon: string; color: string }
> = {
  TERMINATION: { label: "Termination", icon: "ti-circle-minus", color: "#C0392B" },
  DEATH: { label: "Death Case", icon: "ti-heart-off", color: "#5D6D7E" },
  DEPUTATION: { label: "Deputation", icon: "ti-arrows-left-right", color: "#993C1D" },
  RETIREMENT: { label: "Retirement", icon: "ti-hourglass-high", color: "#4A1A6E" },
  TRANSFER_OUT: { label: "Transfer-Out", icon: "ti-arrow-right-circle", color: "#185FA5" },
};

const HrInitiatedCases: React.FC = () => {
  const navigate = useNavigate();
  const routes = all_routes;

  const [cases, setCases] = useState<HrInitiatedCaseItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<Filter>("All");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await getHrInitiatedCases();
      setCases(list);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // HR fills the No-Dues form on the employee's behalf (an HR-initiated case has
  // no employee-side form). Submitting it sends the No-Dues straight to the
  // departments.
  const fillNoDues = (item: HrInitiatedCaseItem) => {
    navigate(routes.Esepration.noDuesForm, {
      state: { request_id: item.id, hrFill: true },
    });
  };

  const filtered = cases.filter((c) => {
    if (filter === "All") return true;
    if (filter === "Termination") return c.request_type === "TERMINATION";
    if (filter === "Death") return c.request_type === "DEATH";
    if (filter === "Deputation") return c.request_type === "DEPUTATION";
    if (filter === "Retirement") return c.request_type === "RETIREMENT";
    if (filter === "Transfer-Out") return c.request_type === "TRANSFER_OUT";
    return true;
  });

  const openCase = (item: HrInitiatedCaseItem) => {
    // A No-Dues-only case has no resignation form / approval flow, so it opens
    // its own No-Dues case screen; an older case with a real approval chain still
    // opens the approval view.
    if (item.display_status) {
      navigate(routes.Esepration.noDuesCaseDetail.replace(":id", item.id), {
        state: { request_id: item.id, role: "HR" },
      });
      return;
    }
    navigate(routes.Esepration.approvalDetail.replace(":id", item.id), {
      state: { request_id: item.id, type: item.type, role: "HR" },
    });
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageHeader title="Initiated Cases" onExit={() => navigate(-1)} />

        {/* Header */}
        <div
          className="card border-0 shadow-sm mb-3"
          style={{ borderRadius: 12 }}
        >
          <div className="card-body d-flex align-items-center gap-3">
            <span
              className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: "#E5F0FA",
                color: "#185FA5",
              }}
            >
              <i className="ti ti-briefcase" style={{ fontSize: 20 }} />
            </span>
            <div className="flex-grow-1">
              <div className="fw-semibold">HR-Initiated Separation Cases</div>
              <small className="text-muted">
                Termination, death, deputation, retirement and
                transfer-out cases (No-Dues only)
              </small>
            </div>
            <button
              className="btn btn-primary d-inline-flex align-items-center justify-content-center p-0"
              style={{ width: 34, height: 34, borderRadius: 10 }}
              title="Initiate a Case"
              onClick={() => navigate(routes.Esepration.hrInitiateCase)}
            >
              <i className="ti ti-plus" style={{ fontSize: 18 }} />
            </button>
          </div>
        </div>

        {/* Filter chips */}
        <div className="d-flex flex-wrap gap-2 mb-3">
          {FILTERS.map((f) => {
            const active = filter === f;
            return (
              <button
                key={f}
                className={`btn btn-sm ${active ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={() => setFilter(f)}
              >
                {f}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-secondary" role="status" />
            <p className="text-muted mt-2 mb-0">Loading cases…</p>
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="ti-file-off"
            title="No initiated cases yet."
            action={
              <button
                className="btn btn-primary mt-2"
                onClick={() => navigate(routes.Esepration.hrInitiateCase)}
              >
                <i className="ti ti-plus me-1" />
                Initiate a Case
              </button>
            }
          />
        ) : (
          <div className="row g-3">
            {filtered.map((item) => {
              const meta =
                TYPE_META[item.request_type] || {
                  label: item.type,
                  icon: "ti-file-text",
                  color: "#6c757d",
                };
              const terminal = ["REJECTED", "WITHDRAWN", "APPROVED"].includes(
                String(item.status ?? "").toUpperCase()
              );
              const showNoDues = !terminal;
              return (
                <div className="col-md-6 col-xl-4" key={item.id}>
                  <div
                    className="card border-0 shadow-sm h-100"
                    style={{ borderRadius: 16 }}
                  >
                    <div className="card-body">
                      <div
                        className="d-flex align-items-center gap-2"
                        style={{ cursor: "pointer" }}
                        onClick={() => openCase(item)}
                      >
                        <span
                          className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: 10,
                            background: `${meta.color}15`,
                            color: meta.color,
                          }}
                        >
                          <i className={`ti ${meta.icon}`} style={{ fontSize: 18 }} />
                        </span>
                        <div className="flex-grow-1">
                          <div className="fw-semibold">{meta.label}</div>
                          <small className="text-muted">{item.id}</small>
                        </div>
                        <StatusBadge status={item.display_status || item.status} />
                      </div>

                      <hr className="my-3" />

                      <div
                        className="d-flex align-items-center"
                        style={{ cursor: "pointer" }}
                        onClick={() => openCase(item)}
                      >
                        <div className="flex-grow-1">
                          <div className="fw-medium">{item.emp_name}</div>
                          <small className="text-muted">
                            {item.emp_no}
                            {item.designation ? `  ·  ${item.designation}` : ""}
                          </small>
                          {item.date && (
                            <div className="small text-muted mt-1">
                              Initiated {item.date}
                            </div>
                          )}
                        </div>
                        <i className="ti ti-chevron-right text-muted" />
                      </div>

                      {/* No-Dues state of the case. Until the No-Dues form is
                          submitted HR can fill it on the employee's behalf (for an
                          HR-initiated case this is the only way; for an
                          employee-initiated one it covers an employee who has not
                          filled it). Once submitted it shows "No-Dues Started". */}
                      {showNoDues &&
                        (item.no_dues_started ? (
                          <div
                            className="d-flex align-items-center justify-content-center gap-2 mt-3 py-2 rounded"
                            style={{
                              border: "1px solid #0F6E56",
                              background: "#E1F5EE",
                              color: "#0F6E56",
                            }}
                          >
                            <i className="ti ti-circle-check" />
                            <span className="fw-semibold small">
                              No-Dues Started
                            </span>
                          </div>
                        ) : (
                          <button
                            className="btn btn-outline-primary w-100 mt-3 d-inline-flex align-items-center justify-content-center gap-2"
                            onClick={() => fillNoDues(item)}
                          >
                            <i className="ti ti-pencil-plus" />
                            Fill No-Dues Form
                          </button>
                        ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default HrInitiatedCases;
